import { useState, useEffect, useMemo } from "react";
import api from '../api/axios';
import { useParams, useNavigate } from "react-router-dom";
import { FaDna, FaCheck, FaTimes, FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "../context/LanguageContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import "../styles/common.css";
import "./ModifierReproduction.css";

export default function ModifierReproduction() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    date_chaleur: "",
    date_saillie: "",
    date_velage: "",
    nb_veaux: 1,
    statut: "en_cours",
    notes: "",
    mere: "",
    pere: "",
  });
  const [initialData, setInitialData] = useState(null);
  const [animaux, setAnimaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    mere: "",
    date_chaleur: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    mere: false,
    date_chaleur: false,
  });

  const STATUT_CHOICES = useMemo(() => [
    { value: "en_cours", label: t.statusInProgress },
    { value: "termine", label: t.statusCompleted },
    { value: "echec", label: t.statusFailed },
  ], [t]);

  useEffect(() => {
    const fetchAnimaux = async () => {
      try {
        const res = await api.get("/api/animaux/");
        setAnimaux(res.data);
      } catch (err) {
        console.error("Erreur lors du chargement des animaux:", err);
      }
    };
    fetchAnimaux();
  }, []);

  useEffect(() => {
    const fetchRepro = async () => {
      try {
        const response = await api.get(`/api/reproductions/${id}/`);
        setFormData(response.data);
        setInitialData(JSON.parse(JSON.stringify(response.data)));
        setError(null);
      } catch (error) {
        console.error("Erreur lors du chargement:", error);
        setError("Erreur lors du chargement de la reproduction.");
      } finally {
        setLoading(false);
      }
    };
    fetchRepro();
  }, [id]);

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  // Validation d'un champ spécifique
  const validateField = (name, value) => {
    if (name === "mere" || name === "date_chaleur") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === "number" ? Number(value) : value,
    });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Valider le champ
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const hasChanges = () => {
    if (!initialData) return false;
    
    // Normaliser les valeurs pour la comparaison (null/undefined -> "")
    const normalize = (val) => (val === null || val === undefined) ? "" : String(val).trim();
    
    return (
      normalize(formData.mere) !== normalize(initialData.mere) ||
      normalize(formData.pere) !== normalize(initialData.pere) ||
      normalize(formData.date_chaleur) !== normalize(initialData.date_chaleur) ||
      normalize(formData.date_saillie) !== normalize(initialData.date_saillie) ||
      normalize(formData.date_velage) !== normalize(initialData.date_velage) ||
      formData.nb_veaux !== initialData.nb_veaux ||
      formData.statut !== initialData.statut ||
      normalize(formData.notes) !== normalize(initialData.notes)
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!hasChanges()) {
      return;
    }
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      mere: true,
      date_chaleur: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      mere: validateField("mere", formData.mere),
      date_chaleur: validateField("date_chaleur", formData.date_chaleur),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/api/reproductions/${id}/`, formData);
      setShowConfirmModal(false);
      toast.success("Reproduction modifiée avec succès !");
      setTimeout(() => {
        navigate("/reproductions");
      }, 500);
    } catch (err) {
      console.error("Erreur lors de la mise à jour:", err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message ||
                          Object.values(err.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la mise à jour.";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/reproductions/${id}/`);
      setShowDeleteModal(false);
      toast.success("Reproduction supprimée avec succès !");
      setTimeout(() => {
        navigate("/reproductions");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la suppression.";
      toast.error(errorMessage);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const getAnimalMatricule = (animalId) => {
    if (!animalId) return "Non sélectionné";
    const animal = animaux.find(a => a.id === parseInt(animalId));
    return animal ? animal.matricule : "Non sélectionné";
  };

  const getStatutLabel = (statut) => {
    const choice = STATUT_CHOICES.find(c => c.value === statut);
    return choice ? choice.label : statut;
  };

  if (loading) {
    return (
      <div className="modifier-reproduction-page">
        <div className="loading-container">
          <p className="loading-text">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modifier-reproduction-page">
        <div className="title-badge">
          <FaDna className="page-title-icon" aria-hidden="true" />
          <span>ERREUR</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modifier-reproduction-page">
      <div className="title-badge">
        <FaDna className="page-title-icon" aria-hidden="true" />
        <span>MODIFIER LA REPRODUCTION</span>
      </div>

      <form onSubmit={handleSubmit} className="modifier-reproduction-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>Champs obligatoires :</strong>
            <span className="required-indicator">*</span> Les champs marqués d'un astérisque rouge sont obligatoires.
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="mere">
              Génitrice <span className="required-indicator">*</span>
            </label>
            <select 
              id="mere"
              name="mere" 
              value={formData.mere || ""} 
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.mere && touchedFields.mere ? "input-error" : ""}
            >
              <option value="">{t.selectOption}</option>
              {animaux
                .filter(animal => animal.sexe === "F")
                .map(animal => (
                  <option key={animal.id} value={animal.id}>
                    {animal.matricule}
                  </option>
                ))}
            </select>
            {fieldErrors.mere && touchedFields.mere && (
              <span className="field-error-message">{fieldErrors.mere}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="pere">{t.sireLabel}</label>
            <select 
              id="pere"
              name="pere" 
              value={formData.pere || ""} 
              onChange={handleChange}
            >
              <option value="">{t.selectOptional}</option>
              {animaux
                .filter(animal => animal.sexe === "M")
                .map(animal => (
                  <option key={animal.id} value={animal.id}>
                    {animal.matricule}
                  </option>
                ))}
            </select>
          </div>

          <div className="form">
            <label htmlFor="date_chaleur">
              {t.heatDate} <span className="required-indicator">*</span>
            </label>
            <input 
              type="date" 
              id="date_chaleur"
              name="date_chaleur" 
              value={formData.date_chaleur || ""} 
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.date_chaleur && touchedFields.date_chaleur ? "input-error" : ""}
            />
            {fieldErrors.date_chaleur && touchedFields.date_chaleur && (
              <span className="field-error-message">{fieldErrors.date_chaleur}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="date_saillie">{t.breedingDate}</label>
            <input 
              type="date" 
              id="date_saillie"
              name="date_saillie" 
              value={formData.date_saillie || ""} 
              onChange={handleChange}
            />
          </div>

          <div className="form">
            <label htmlFor="statut">{t.statusLabel}</label>
            <select 
              id="statut"
              name="statut" 
              value={formData.statut} 
              onChange={handleChange}
            >
              {STATUT_CHOICES.map(choice => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form">
            <label htmlFor="date_velage">Date de vêlage</label>
            <input 
              type="date" 
              id="date_velage"
              name="date_velage" 
              value={formData.date_velage || ""} 
              onChange={handleChange}
            />
          </div>

          <div className="form">
            <label htmlFor="nb_veaux">{t.calvesNumber}</label>
            <input 
              type="number" 
              id="nb_veaux"
              name="nb_veaux" 
              value={formData.nb_veaux} 
              onChange={handleChange} 
              min="0"
            />
          </div>

          <div className="form full-width">
            <label htmlFor="notes">{t.notes}</label>
            <textarea 
              id="notes"
              name="notes" 
              value={formData.notes || ""} 
              onChange={handleChange}
              placeholder={t.reproductionNotes}
              rows="3"
            />
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!hasChanges() || submitting}
          >
            <FaCheck />
            <span>{submitting ? "Enregistrement..." : "Enregistrer"}</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/reproductions")}
            disabled={submitting}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
          <button
            type="button"
            className="delete-but"
            onClick={() => setShowDeleteModal(true)}
            disabled={submitting}
          >
            <FaTrash />
            <span>Supprimer</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer la modification</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les modifications avant de confirmer :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">Génitrice :</span>
                  <span className="summary-value">{getAnimalMatricule(formData.mere) || "Non renseigné"}</span>
                </div>
                {formData.pere && (
                  <div className="summary-row">
                    <span className="summary-label">Géniteur :</span>
                    <span className="summary-value">{getAnimalMatricule(formData.pere)}</span>
                  </div>
                )}
                {formData.date_chaleur && (
                  <div className="summary-row">
                    <span className="summary-label">Date de chaleur :</span>
                    <span className="summary-value">{formData.date_chaleur}</span>
                  </div>
                )}
                {formData.date_saillie && (
                  <div className="summary-row">
                    <span className="summary-label">Date de saillie :</span>
                    <span className="summary-value">{formData.date_saillie}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span className="summary-label">Statut :</span>
                  <span className="summary-value">{getStatutLabel(formData.statut)}</span>
                </div>
                {formData.date_velage && (
                  <div className="summary-row">
                    <span className="summary-label">Date de vêlage :</span>
                    <span className="summary-value">{formData.date_velage}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span className="summary-label">Nombre de veaux :</span>
                  <span className="summary-value">{formData.nb_veaux}</span>
                </div>
                {formData.notes && (
                  <div className="summary-row">
                    <span className="summary-label">Notes :</span>
                    <span className="summary-value">{formData.notes}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={submitting}
              >
                <FaTimes />
                <span>Annuler</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={submitting}
              >
                <FaCheck />
                <span>{submitting ? "Enregistrement..." : "Confirmer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        itemName=""
        itemType="reproduction"
        consequences={[]}
        requireConfirmation={false}
        isLoading={isDeleting}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
